/*
  Kiwi Ears · Wave Arcs canvas background (Rene 2026-08-22).

  Ported from the Originkit "Wave Arcs" React component supplied by Rene. The
  drawing maths is carried over verbatim — line count, the tan() sweep, the
  brightness ramp, the two arc spans, the mobile reductions and the 40/60px
  bottom offset all behave as they do in the original. What changed is only the
  plumbing, because this theme is Liquid + plain JS, not React:

   · the hook's lifecycle (setup / IntersectionObserver / visibilitychange /
     debounced resize / requestIdleCallback deferral) is kept, hand-rolled.
   · options arrive as data-* attributes instead of props, so any section can
     mount one from Liquid without a build step.
   · PREFERS-REDUCED-MOTION: the original always animates. Here a reduced-motion
     visitor gets one static frame and no rAF loop at all — house rule, and the
     arcs read fine as a still image.
   · devicePixelRatio is capped at 2. At 76 lines × 2 spans × 200+ segments a
     3x phone would be drawing ~9x the pixels of a 1x desktop every frame for no
     visible gain.

  Markup contract:

    <div data-wavearcs
         data-background="#F3FDF9" data-line="#A9FFA6"
         data-line-width="1" data-line-count="76"
         data-speed="5" data-glow="10" data-interactive="true">
      <canvas></canvas>
    </div>

  The host element must be sized by its own CSS (this file never touches layout).
*/
(function () {
  var TWO_PI = Math.PI * 2;

  function map(v, a, b, c, d) { return ((v - a) / (b - a)) * (d - c) + c; }

  function parseRGB(str) {
    if (!str) return { r: 255, g: 255, b: 255 };
    var m = str.match(/rgba?\(([^)]+)\)/i);
    if (m) {
      var p = m[1].split(',').map(function (n) { return parseInt(n.trim(), 10); });
      return { r: p[0], g: p[1], b: p[2] };
    }
    var hex = str.replace(/^#/, '');
    if (hex.length === 3) hex = hex[0] + hex[0] + hex[1] + hex[1] + hex[2] + hex[2];
    if (hex.length >= 6) {
      return {
        r: parseInt(hex.slice(0, 2), 16),
        g: parseInt(hex.slice(2, 4), 16),
        b: parseInt(hex.slice(4, 6), 16)
      };
    }
    return { r: 255, g: 255, b: 255 };
  }

  function num(host, key, fallback) {
    var v = parseFloat(host.getAttribute('data-' + key));
    return isNaN(v) ? fallback : v;
  }

  function mount(host) {
    var canvas = host.querySelector('canvas');
    if (!canvas) return;
    var ctx = canvas.getContext('2d', { alpha: false });
    if (!ctx) return;

    var background = host.getAttribute('data-background') || '#F3FDF9';
    var lineColor = host.getAttribute('data-line') || '#A9FFA6';
    var lineWidth = num(host, 'line-width', 1);
    var lineCount = num(host, 'line-count', 76);
    var speed = num(host, 'speed', 5);
    var glow = num(host, 'glow', 10);
    var interactive = host.getAttribute('data-interactive') !== 'false';
    var rgb = parseRGB(lineColor);

    var still = window.matchMedia &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    var st = { width: 0, height: 0, dpr: 1, isVisible: true, isPageVisible: true, id: 0, frame: 0 };
    var mouse = { y: 0, targetY: 0 };
    var rect = null;

    function setup() {
      var dpr = Math.min(window.devicePixelRatio || 1, 2);
      rect = host.getBoundingClientRect();
      st.width = rect.width;
      st.height = rect.height;
      st.dpr = dpr;
      canvas.width = Math.round(rect.width * dpr);
      canvas.height = Math.round(rect.height * dpr);
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    }

    function draw() {
      var r = st.width, i = st.height;
      if (!r || !i) return;

      mouse.y = mouse.y + (mouse.targetY - mouse.y) * 0.1;

      ctx.fillStyle = background;
      ctx.fillRect(0, 0, r, i);

      var isMobile = r < 768;
      var u = 55000 / glow;

      ctx.save();
      ctx.lineWidth = lineWidth;
      ctx.translate(r / 2, i + (isMobile ? 60 : 40));

      var f = interactive ? map(mouse.y, 0, i, 1.2, -1.2) : 0;
      var m = Math.max(320, Math.min(1440, r));
      var rate = map(m, 320, 1440, 0.002, 5e-4) * (speed / 5);
      var p = st.frame * rate;
      var h = r / 2;
      var x = isMobile ? Math.round(lineCount * 0.6) : lineCount;

      for (var k = 0; k < x; k++) {
        var ang = map(k, 0, x, 0, Math.PI) + p;
        ang %= Math.PI;
        var l = (Math.tan(ang) - f) * i;
        var a = Math.abs(l) / 2;
        var yCenter = -i / 2 + l / 2;
        var bright = Math.max(0, Math.min(255, map(Math.abs(l), 0, u, -20, 255))) / 255;
        if (bright <= 0) continue;

        ctx.strokeStyle = 'rgba(' + rgb.r + ', ' + rgb.g + ', ' + rgb.b + ', ' + bright + ')';

        if (a > 499999.5) {           /* the arc has flattened into a line */
          ctx.beginPath();
          ctx.moveTo(-h, -i / 2);
          ctx.lineTo(h, -i / 2);
          ctx.stroke();
          continue;
        }

        var c2 = Math.acos(Math.min(1, (h + 50) / a));
        var segTotal = Math.max(Math.ceil(a / 120), 200);
        var spans = [[c2, Math.PI - c2], [Math.PI + c2, TWO_PI - c2]];
        for (var si = 0; si < spans.length; si++) {
          var start = spans[si][0], end = spans[si][1];
          var span = end - start;
          var n3 = Math.max(Math.ceil((span / TWO_PI) * segTotal), 60);
          var step = span / n3;
          ctx.beginPath();
          for (var sg = 0; sg <= n3; sg++) {
            var aa = start + step * sg;
            var xx = Math.cos(aa) * a;
            var yy = yCenter + Math.sin(aa) * a;
            if (sg === 0) ctx.moveTo(xx, yy); else ctx.lineTo(xx, yy);
          }
          ctx.stroke();
        }
      }

      ctx.restore();
    }

    function loop() {
      st.frame += 1;
      draw();
      st.id = requestAnimationFrame(loop);
    }

    function start() {
      if (still || st.id || !st.isVisible || !st.isPageVisible || !canStart) return;
      st.id = requestAnimationFrame(loop);
    }
    function stop() {
      if (st.id) { cancelAnimationFrame(st.id); st.id = 0; }
    }

    setup();
    mouse.targetY = st.height / 2;
    mouse.y = st.height / 2;

    if (still) {                     /* one frame, then nothing */
      draw();
      window.addEventListener('resize', function () { setup(); draw(); }, { passive: true });
      return;
    }

    /* deferred start: the arcs are decoration, so they wait for an idle moment */
    var canStart = false;
    function begin() { canStart = true; start(); }
    if (window.requestIdleCallback) window.requestIdleCallback(begin);
    else setTimeout(begin, 150);

    var resizeTimer;
    window.addEventListener('resize', function () {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(function () { setup(); }, 100);
    }, { passive: true });

    document.addEventListener('visibilitychange', function () {
      st.isPageVisible = document.visibilityState === 'visible';
      if (st.isVisible && st.isPageVisible) start(); else stop();
    });

    if (window.IntersectionObserver) {
      new IntersectionObserver(function (entries) {
        st.isVisible = entries[0] ? entries[0].isIntersecting : true;
        if (st.isVisible && st.isPageVisible) start(); else stop();
      }, { threshold: 0 }).observe(host);
    }

    if (interactive) {
      document.addEventListener('mousemove', function (ev) {
        if (st.isVisible && rect) mouse.targetY = ev.clientY - rect.top;
      }, { passive: true });
      var rafId = 0;
      window.addEventListener('scroll', function () {
        if (rafId) return;
        rafId = requestAnimationFrame(function () {
          rect = host.getBoundingClientRect();
          rafId = 0;
        });
      }, { passive: true });
    }
  }

  function init() {
    var hosts = [].slice.call(document.querySelectorAll('[data-wavearcs]'));
    hosts.forEach(mount);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
